import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useDenyChime — plays a subtle two-tone chime whenever a new DENY decision arrives.
 *
 * Behaviour:
 *  - Toggle persisted to localStorage
 *  - Uses Web Audio API (no external audio asset)
 *  - Throttles to 1 chime per 1.5s so a burst of denies doesn't spam
 *  - First activation call must originate from a user gesture so browsers allow audio
 *
 * Args:
 *  - decisions: newest-first array of decision objects (must have {id, action})
 *
 * Returns:
 *  - enabled, toggle(), testPlay()
 */
export default function useDenyChime(decisions) {
  const STORAGE_KEY = "sentry_deny_chime_v1";
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const ctxRef = useRef(null);
  const lastPlayedRef = useRef(0);
  const seenRef = useRef(new Set());

  const ensureCtx = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctxRef.current = new AC();
    return ctxRef.current;
  }, []);

  const chime = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;

    // Two-tone bell: 880Hz → 587Hz, with a soft envelope.
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.22, now + 0.03);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
    master.connect(ctx.destination);

    const tone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(1, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(master);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    };

    tone(880, 0.0, 0.28);
    tone(587, 0.15, 0.45);
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* noop */ }
      if (next) {
        // Prime audio context inside the user-gesture call
        const ctx = ensureCtx();
        if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
        // Tiny confirmation blip
        try { chime(); lastPlayedRef.current = Date.now(); } catch { /* noop */ }
      }
      return next;
    });
  }, [ensureCtx, chime]);

  const testPlay = useCallback(() => {
    const ctx = ensureCtx();
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    chime();
    lastPlayedRef.current = Date.now();
  }, [ensureCtx, chime]);

  // Watch for new DENYs
  useEffect(() => {
    if (!enabled) return;
    if (!decisions || decisions.length === 0) return;
    const newest = decisions[0];
    if (!newest?.id || seenRef.current.has(newest.id)) return;
    seenRef.current.add(newest.id);
    if (newest.action !== "deny") return;
    const now = Date.now();
    if (now - lastPlayedRef.current < 1500) return; // throttle
    lastPlayedRef.current = now;
    try { chime(); } catch { /* noop */ }
  }, [decisions, enabled, chime]);

  // Ensure the seen set doesn't grow unbounded
  useEffect(() => {
    if (seenRef.current.size > 500) {
      seenRef.current = new Set(Array.from(seenRef.current).slice(-200));
    }
  }, [decisions]);

  return { enabled, toggle, testPlay };
}
